import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Layers, X, Loader2, ChevronDown, XCircle, Pencil } from 'lucide-react';

import {
    fetchSubDCAGrid, fetchItCodesLookup, fetchApprovalStatusSDCA, submitUpdateSubDCA,
    clearUpdateResult, resetSubDca, selectSubDca,
} from '../../slices/accountsSlice/subDcaSlice';

// ── Shared primitives (matches AssignCCandDCABudget / AddNewITCode / DCACreation) ─
const inputCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60';
const selectCls = inputCls + ' appearance-none pr-9 cursor-pointer';
const readonlyCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectWrap = ({ children, loading }) => (
    <div className="relative">
        {children}
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

const Btn = ({ onClick, disabled, loading, variant = 'primary', children }) => {
    const base = 'inline-flex items-center gap-2 font-semibold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed px-4 py-2 text-sm';
    const variants = {
        primary: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm',
        secondary: 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-indigo-400',
    };
    return (
        <button type="button" onClick={onClick} disabled={disabled || loading} className={`${base} ${variants[variant]}`}>
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {children}
        </button>
    );
};

const Badge = ({ children, color = 'gray' }) => {
    const colors = {
        green: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-700',
        red: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-700',
        amber: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-700',
        gray: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${colors[color] || colors.gray}`}>{children}</span>;
};

const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm ${className}`}>{children}</div>
);

const EmptyState = ({ message }) => <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">{message}</div>;

const Modal = ({ title, onClose, children }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl sticky top-0 z-10">
                <h2 className="text-sm font-bold text-white">{title}</h2>
                <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
            </div>
            <div className="p-5">{children}</div>
        </div>
    </div>
);

const statusBadge = (status) => {
    if (status === 'Approved') return <Badge color="green">Approved</Badge>;
    if (status === 'Rejected') return <Badge color="red">Rejected</Badge>;
    return <Badge color="amber">Pending</Badge>;
};

// ═════════════════════════════════════════════════════════════════════════════
// EDIT SUB DCA MODAL
// ═════════════════════════════════════════════════════════════════════════════

const EditSubDCAModal = ({ row, onClose, currentUser, roleId, itCodes, itCodesLoading }) => {
    const dispatch = useDispatch();
    const { updateLoading, updateResult, updateError } = useSelector(selectSubDca);

    const [subDcaName, setSubDcaName] = useState(row.SubDCAName || '');
    const [itCodeId, setItCodeId] = useState(row.ITCodeID ? String(row.ITCodeID) : '');

    useEffect(() => {
        if (updateResult) { toast.success('Sub Account Head updated successfully.'); dispatch(clearUpdateResult()); onClose(); }
        if (updateError) { toast.error(updateError); dispatch(clearUpdateResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateResult, updateError]);

    const handleSubmit = () => {
        if (!subDcaName.trim()) { toast.warning('Enter Sub Account Head Name.'); return; }
        if (!itCodeId) { toast.warning('Select an IT Code.'); return; }

        dispatch(submitUpdateSubDCA({
            DCACodeID: row.DCACodeID,
            SubDCACodeID: row.SubDCACodeID,
            SubDCACode: row.SubDCACode,
            SubDCAName: subDcaName.trim(),
            ITCodeID: parseInt(itCodeId, 10),
            Createdby: currentUser,
            RoleId: parseInt(roleId, 10) || 0,
            CheckUpdationType: 'Insert',
        }));
    };

    return (
        <Modal title={`Edit Sub Account Head — ${row.SubDCACode}`} onClose={onClose}>
            <div className="grid grid-cols-1 gap-4">
                <div>
                    <Label>Account Head Code</Label>
                    <input className={readonlyCls} value={row.DCAName || ''} readOnly />
                </div>
                <div>
                    <Label>Sub Account Head Code</Label>
                    <input className={readonlyCls} value={row.SubDCACode || ''} readOnly />
                </div>
                <div>
                    <Label required>Sub Account Head Name</Label>
                    <input className={inputCls} value={subDcaName} onChange={(e) => setSubDcaName(e.target.value)} disabled={updateLoading} />
                </div>
                <div>
                    <Label required>IT Code</Label>
                    <SelectWrap loading={itCodesLoading}>
                        <select className={selectCls} value={itCodeId} onChange={(e) => setItCodeId(e.target.value)} disabled={updateLoading}>
                            <option value="">-Please Select-</option>
                            {itCodes.map((i) => <option key={i.ITCodeID} value={i.ITCodeID}>{i.ITCodename}</option>)}
                        </select>
                    </SelectWrap>
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={updateLoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={updateLoading}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════

const SubDCAEdit = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const currentUser = userData?.userName || userData?.userId || userData?.UID || userData?.uid || '';

    const { grid, gridLoading, gridError, itCodes, itCodesLoading, allowedActions } = useSelector(selectSubDca);

    const [editRow, setEditRow] = useState(null);

    useEffect(() => {
        dispatch(fetchSubDCAGrid());
        dispatch(fetchItCodesLookup());
        dispatch(fetchApprovalStatusSDCA(roleId));
        return () => { dispatch(resetSubDca()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const canAct = (row) => row.Status === 'Approved' && (!row.CloseStatus || row.CloseStatus === 'Rejected');

    const refreshGrid = () => dispatch(fetchSubDCAGrid());

    const handleActionChange = (row, action) => {
        if (!action || action === 'Select' || action === '0') return;
        if (action === 'Edit') { setEditRow(row); return; }
        toast.info(`"${action}" isn't available on this page yet.`);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 px-6 py-8 shadow-lg">
                <div className="max-w-7xl mx-auto flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Layers className="h-5 w-5 text-white" /></div>
                    <div>
                        <h1 className="text-xl font-bold text-white">{menuData?.name || 'Sub-Account Heads'}</h1>
                        <p className="text-indigo-200 text-xs mt-0.5">View and edit Sub Account Heads</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
                <Card>
                    {gridLoading ? (
                        <div className="py-14 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                    ) : gridError ? (
                        <div className="py-10 text-center text-sm text-rose-500">{gridError}</div>
                    ) : grid.length === 0 ? (
                        <EmptyState message="No Sub Account Heads found." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        {['Account Head Code', 'Sub Account Head Code', 'Sub Account Head Name', 'Status', ''].map((h) => (
                                            <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {grid.map((row) => (
                                        <tr key={row.SubDCACodeID} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                            <td className="px-3 py-2.5 font-mono text-gray-700 dark:text-gray-300">{row.DCAName}</td>
                                            <td className="px-3 py-2.5 font-mono text-gray-700 dark:text-gray-300">{row.SubDCACode}</td>
                                            <td className="px-3 py-2.5 text-gray-800 dark:text-gray-100">{row.SubDCAName}</td>
                                            <td className="px-3 py-2.5">{statusBadge(row.Status)}</td>
                                            <td className="px-3 py-2.5">
                                                {canAct(row) ? (
                                                    allowedActions.length > 0 ? (
                                                        <SelectWrap>
                                                            <select
                                                                className={selectCls + ' py-1 text-xs'}
                                                                defaultValue=""
                                                                onChange={(e) => { handleActionChange(row, e.target.value); e.target.value = ''; }}
                                                            >
                                                                <option value="">-Select-</option>
                                                                {allowedActions.map((a, i) => <option key={i} value={a.Value}>{a.Text}</option>)}
                                                            </select>
                                                        </SelectWrap>
                                                    ) : (
                                                        <button onClick={() => setEditRow(row)} className="p-1.5 rounded-lg text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title="Edit">
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </button>
                                                    )
                                                ) : (
                                                    <span className="text-xs text-gray-300 dark:text-gray-600 inline-flex items-center gap-1">
                                                        <XCircle className="h-3 w-3" /> —
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            {editRow && (
                <EditSubDCAModal
                    row={editRow} onClose={() => { setEditRow(null); refreshGrid(); }}
                    currentUser={currentUser} roleId={roleId}
                    itCodes={itCodes} itCodesLoading={itCodesLoading}
                />
            )}
        </div>
    );
};

export default SubDCAEdit;
